import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Bot, Zap, Shield, Database, BarChart3, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const HubSpotAIAgent = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Agent for HubSpot",
      "applicationCategory": "BusinessApplication",
      "description": "The best AI agent for HubSpot CRM. Automatically update deals, enrich contacts, log activities, and forecast pipeline — without manual HubSpot data entry.",
      "url": "https://noq5.ai/hubspot-ai-agent"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "HubSpot AI Agent", "item": "https://noq5.ai/hubspot-ai-agent" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is the best AI agent for HubSpot?", "acceptedAnswer": { "@type": "Answer", "text": "NoQ5 is the leading AI agent for HubSpot that autonomously updates deals, enriches contacts, logs activities from all channels, and generates pipeline forecasts — going far beyond HubSpot's native Breeze AI capabilities." } },
        { "@type": "Question", "name": "How do I add AI agents to HubSpot?", "acceptedAnswer": { "@type": "Answer", "text": "Connect NoQ5 to your HubSpot account via OAuth in under 5 minutes. The AI agent auto-detects your HubSpot properties, pipeline stages, and custom fields, then begins capturing activities and updating records autonomously." } },
        { "@type": "Question", "name": "Can AI update HubSpot deals automatically?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. NoQ5's AI agent listens to your sales calls, reads emails, and monitors Slack — then automatically updates HubSpot deal properties including stage, amount, close date, next steps, and 40+ other fields." } }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="HubSpot AI Agent — Automate HubSpot with AI | NoQ5"
        description="The #1 AI agent for HubSpot. Automatically update deals, enrich contacts, log activities from all channels, and forecast pipeline. Goes beyond Breeze AI. Setup in 5 minutes."
        keywords="HubSpot AI agent, AI agent for HubSpot, HubSpot automation AI, HubSpot AI CRM, automate HubSpot, HubSpot AI assistant, HubSpot deals AI, HubSpot Breeze alternative, AI update HubSpot, HubSpot CRM agent, HubSpot AI integration, best AI for HubSpot"
        canonicalUrl="https://noq5.ai/hubspot-ai-agent"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">HubSpot AI Agent</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">HubSpot AI Agent</span>: Make HubSpot Run Itself
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              HubSpot Breeze gives you AI suggestions. NoQ5 gives you an AI agent that <em>actually does the work</em> — updating deals, enriching contacts, logging every activity, and forecasting your pipeline. Connected to HubSpot in 5 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial"><Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Connect HubSpot <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              <Link to="/noq5-vs-hubspot-ai"><Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">NoQ5 vs HubSpot AI</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">What the AI Agent Does Inside HubSpot</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Bot, title: "Auto-Update Deal Properties", desc: "After every call and email, the agent updates deal stage, amount, close date, next steps, and custom properties — mapped to your HubSpot pipeline configuration." },
              { icon: Database, title: "Enrich Contact Records", desc: "Fill missing HubSpot contact properties — job title, company size, industry, phone, LinkedIn — from 15+ sources. Continuously refreshed, no Breeze Intelligence credits needed." },
              { icon: Zap, title: "Capture All Activities", desc: "Log calls, emails, Zoom meetings, Slack messages, and LinkedIn interactions to the correct HubSpot contact and deal — automatically." },
              { icon: BarChart3, title: "Generate Pipeline Forecasts", desc: "Evidence-based forecasts from actual engagement data. No more relying on rep-submitted deal probabilities in HubSpot." },
              { icon: Users, title: "Multi-Thread Detection", desc: "Track how many stakeholders are engaged per deal. Alert when deals are single-threaded — the #1 predictor of deal failure." },
              { icon: Shield, title: "Keep HubSpot Clean", desc: "Enforce property completeness, standardize values, detect duplicates, and maintain consistent data quality across your entire HubSpot instance." },
            ].map((item) => (
              <div key={item.title} className="bg-card border border-border rounded-xl p-6">
                <item.icon className="h-8 w-8 text-primary mb-3" />
                <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <h2 className="text-3xl font-bold text-foreground">How to Add an AI Agent to HubSpot</h2>
            <ol className="list-decimal pl-6 space-y-3">
              <li><strong>Connect HubSpot via OAuth</strong> — One click. NoQ5 reads your HubSpot pipelines, deal stages, contact properties, and custom fields automatically.</li>
              <li><strong>Connect your communication tools</strong> — Gmail, Zoom, Slack, LinkedIn, Calendar. The agent starts capturing all sales activities across channels.</li>
              <li><strong>Review auto-detected field mapping</strong> — NoQ5 maps conversation data to your specific HubSpot properties. Customize any mapping in the dashboard.</li>
              <li><strong>Activate</strong> — The AI agent begins updating HubSpot in real-time. Your reps never touch a HubSpot form again.</li>
            </ol>

            <h3 className="text-2xl font-bold text-foreground">Limitations of HubSpot's Native AI (Breeze)</h3>
            <p>HubSpot Breeze is improving, but it has fundamental limitations that a purpose-built <Link to="/crm-agent" className="text-primary hover:underline">CRM AI agent</Link> solves:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>No cross-platform capture</strong> — Breeze can't see your Slack conversations, LinkedIn messages, or external email threads</li>
              <li><strong>Credit-based enrichment</strong> — Breeze Intelligence charges per enrichment; costs add up fast for active sales teams</li>
              <li><strong>Suggestions, not execution</strong> — Breeze suggests next steps; NoQ5 executes them in your CRM automatically</li>
              <li><strong>Limited meeting intelligence</strong> — Basic call transcription without deep signal extraction or automatic CRM field updates</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Make Your HubSpot Run Itself</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">Connect in 5 minutes. 40+ properties auto-updated. Zero manual data entry. Works alongside your existing HubSpot setup.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial"><Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">Connect HubSpot Free <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/crm-agent"><Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">Full CRM Agent Details</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/noq5-vs-hubspot-ai" className="text-primary hover:underline text-sm">NoQ5 vs HubSpot AI</Link>
            <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">Salesforce AI Agent</Link>
            <Link to="/pipedrive-ai-agent" className="text-primary hover:underline text-sm">Pipedrive AI Agent</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/comparison/hubspot" className="text-primary hover:underline text-sm">Full HubSpot Comparison</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HubSpotAIAgent;
